import React, { useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../../util/reactIntl';
import { parse } from '../../../../util/urlHelpers';
import { createResourceLocatorString } from '../../../../util/routes';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { ensureOwnListing } from '../../../../util/data';
import { getMarketplaceEntities } from '../../../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../../../ducks/ui.duck';
import {
  stripeAccountClearError,
  getStripeConnectAccountLink,
} from '../../../../ducks/stripeConnectAccount.duck';

// Import shared components
import { NamedLink } from '../../../../components';

// Import modules from the EditListingPage container so we can reuse the
// exact same create-draft -> publish flow that powers /personalised_offers
import {
  requestFetchAvailabilityExceptions,
  requestAddAvailabilityException,
  requestDeleteAvailabilityException,
  requestCreateListingDraft,
  requestPublishListingDraft,
  requestUpdateListing,
  requestImageUpload,
  removeListingImage,
  savePayoutDetails,
  selectFileUploads,
  selectFileUploadsDisabled,
  selectHasPendingFileUploads,
  selectAllFilesUploadedAndVerified,
  uploadFile,
  clearUploadedFiles,
  downloadFile,
} from '../../../../containers/EditListingPage/EditListingPage.duck';
import EditListingWizard from '../../../../containers/EditListingPage/EditListingWizard/EditListingWizard';

import SectionContainer from '../SectionContainer';
import css from './SectionAlertForm.module.css';

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const DRAFT_ID = '00000000-0000-0000-0000-000000000000';
const DRAFT_SLUG = 'draft';

// The alert wizard only has the "details" tab and always runs as a brand
// new listing flow. Mirrors the params used by the /personalised_offers route.
const ALERT_PARAMS = {
  slug: DRAFT_SLUG,
  id: DRAFT_ID,
  type: 'new',
  tab: 'details',
  listingType: 'alert',
};

// CMS page the user is redirected to once the alert has been created.
const SUCCESS_PAGE_ID = 'request_success';

/**
 * Section that embeds the "create new alert" form (the same one rendered on
 * /personalised_offers) inside a CMS page.
 *
 * @component
 * @returns {JSX.Element}
 */
export const SectionAlertFormComponent = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    appearance,
    options,
    currentUser,
    getOwnListing,
    history,
    location,
    page,
    scrollingDisabled,
    authScopes,
    createStripeAccountError,
    updateStripeAccountError,
    fetchStripeAccountError,
    getAccountLinkError,
    getAccountLinkInProgress,
    stripeAccount,
    stripeAccountFetched,
    fetchInProgress,
    fileUploads,
    fileUploadsDisabled,
    hasPendingFileUploads,
    allFilesUploadedAndVerified,
    onFetchExceptions,
    onAddAvailabilityException,
    onDeleteAvailabilityException,
    onUpdateListing,
    onCreateListingDraft,
    onPublishListingDraft,
    onImageUpload,
    onRemoveListingImage,
    onManageDisableScrolling,
    onPayoutDetailsChange,
    onPayoutDetailsSubmit,
    onGetStripeConnectAccountLink,
    onUploadFile,
    onClearUploadedFiles,
    onDownloadFile,
  } = props;

  const routeConfiguration = useRouteConfiguration();
  const classes = classNames(rootClassName || css.root, className);

  // A successful publish sets page.redirectToListing to true. We only want to
  // react to a fresh false -> true transition that happens while this section
  // is mounted (not to a stale value left over from a previous submit), so we
  // seed the ref with the value present on the first render.
  const wasRedirectToListing = useRef(page.redirectToListing);
  useEffect(() => {
    const previously = wasRedirectToListing.current;
    wasRedirectToListing.current = page.redirectToListing;
    if (!previously && page.redirectToListing) {
      history.push(
        createResourceLocatorString(
          'CMSPage',
          routeConfiguration,
          { pageId: SUCCESS_PAGE_ID },
          {}
        )
      );
    }
  }, [page.redirectToListing, history, routeConfiguration]);

  // Alerts are always created as a brand new listing, so we start from an
  // empty own listing (submittedListingId is filled in once the draft exists).
  const listingId = page.submittedListingId || null;
  const currentListing = ensureOwnListing(getOwnListing(listingId));

  const {
    createListingDraftError = null,
    publishListingError = null,
    updateListingError = null,
    showListingsError = null,
    uploadImageError = null,
    setStockError = null,
    addExceptionError = null,
    deleteExceptionError = null,
  } = page;
  const errors = {
    createListingDraftError,
    publishListingError,
    updateListingError,
    showListingsError,
    uploadImageError,
    setStockError,
    createStripeAccountError,
    addExceptionError,
    deleteExceptionError,
  };

  // The current user must be logged in to create an alert. If not, prompt them
  // to log in / sign up rather than letting the submit fail against the API.
  if (!currentUser?.id) {
    return (
      <SectionContainer
        id={sectionId}
        className={classes}
        appearance={appearance}
        options={options}
      >
        <div className={css.authPrompt}>
          <p className={css.authPromptText}>
            <FormattedMessage id="SectionAlertForm.authPrompt" />
          </p>
          <div className={css.authPromptLinks}>
            <NamedLink name="LoginPage" className={css.authPromptButton}>
              <FormattedMessage id="SectionAlertForm.logIn" />
            </NamedLink>
            <NamedLink name="SignupPage" className={css.authPromptButtonSecondary}>
              <FormattedMessage id="SectionAlertForm.signUp" />
            </NamedLink>
          </div>
        </div>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer
      id={sectionId}
      className={classes}
      appearance={appearance}
      options={options}
    >
      <div className={css.formWrapper}>
        <EditListingWizard
          id="PersonalisedOffersAlertWizard"
          className={css.wizard}
          params={ALERT_PARAMS}
          locationSearch={parse(location.search)}
          disabled={page.redirectToListing && !showListingsError}
          errors={errors}
          fetchInProgress={fetchInProgress}
          newListingPublished={false}
          history={history}
          images={[]}
          listing={currentListing}
          weeklyExceptionQueries={page.weeklyExceptionQueries}
          monthlyExceptionQueries={page.monthlyExceptionQueries}
          allExceptions={page.allExceptions}
          onFetchExceptions={onFetchExceptions}
          onAddAvailabilityException={onAddAvailabilityException}
          onDeleteAvailabilityException={onDeleteAvailabilityException}
          onUpdateListing={onUpdateListing}
          onCreateListingDraft={onCreateListingDraft}
          onPublishListingDraft={onPublishListingDraft}
          onPayoutDetailsChange={onPayoutDetailsChange}
          onPayoutDetailsSubmit={onPayoutDetailsSubmit}
          onGetStripeConnectAccountLink={onGetStripeConnectAccountLink}
          getAccountLinkInProgress={getAccountLinkInProgress}
          onImageUpload={onImageUpload}
          onRemoveImage={onRemoveListingImage}
          currentUser={currentUser}
          onManageDisableScrolling={onManageDisableScrolling}
          updatedTab={page.updatedTab}
          updateInProgress={page.updateInProgress || page.createListingDraftInProgress}
          payoutDetailsSaveInProgress={page.payoutDetailsSaveInProgress}
          payoutDetailsSaved={page.payoutDetailsSaved}
          stripeAccountFetched={stripeAccountFetched}
          stripeAccount={stripeAccount}
          stripeAccountError={
            createStripeAccountError || updateStripeAccountError || fetchStripeAccountError
          }
          stripeAccountLinkError={getAccountLinkError}
          authScopes={authScopes}
          isAlert
          redirectToListing={page.redirectToListing}
          titleId="EditListingPage.titleCreateListing"
          fileUploads={fileUploads}
          fileUploadsDisabled={fileUploadsDisabled}
          hasPendingFileUploads={hasPendingFileUploads}
          allFilesUploadedAndVerified={allFilesUploadedAndVerified}
          onUploadFile={onUploadFile}
          onClearUploadedFiles={onClearUploadedFiles}
          onDownloadFile={onDownloadFile}
        />
      </div>
    </SectionContainer>
  );
};

const mapStateToProps = state => {
  const page = state.EditListingPage;
  const {
    getAccountLinkInProgress,
    getAccountLinkError,
    createStripeAccountInProgress,
    createStripeAccountError,
    updateStripeAccountError,
    fetchStripeAccountError,
    stripeAccount,
    stripeAccountFetched,
  } = state.stripeConnectAccount;

  const getOwnListing = id => {
    if (!id) {
      return null;
    }
    const listings = getMarketplaceEntities(state, [{ id, type: 'ownListing' }]);
    return listings.length === 1 ? listings[0] : null;
  };

  const { authScopes } = state.auth;

  return {
    getAccountLinkInProgress,
    getAccountLinkError,
    createStripeAccountError,
    updateStripeAccountError,
    fetchStripeAccountError,
    stripeAccount,
    stripeAccountFetched,
    currentUser: state.user.currentUser,
    fetchInProgress: createStripeAccountInProgress,
    getOwnListing,
    page,
    scrollingDisabled: isScrollingDisabled(state),
    authScopes,
    fileUploads: selectFileUploads(state),
    fileUploadsDisabled: selectFileUploadsDisabled(state),
    hasPendingFileUploads: selectHasPendingFileUploads(state),
    allFilesUploadedAndVerified: selectAllFilesUploadedAndVerified(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchExceptions: params => dispatch(requestFetchAvailabilityExceptions(params)),
  onAddAvailabilityException: params => dispatch(requestAddAvailabilityException(params)),
  onDeleteAvailabilityException: params => dispatch(requestDeleteAvailabilityException(params)),
  onUpdateListing: (tab, values, config) => dispatch(requestUpdateListing(tab, values, config)),
  onCreateListingDraft: (values, config) => dispatch(requestCreateListingDraft(values, config)),
  onPublishListingDraft: listingId => dispatch(requestPublishListingDraft(listingId)),
  onImageUpload: (data, listingImageConfig) =>
    dispatch(requestImageUpload(data, listingImageConfig)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onPayoutDetailsChange: () => dispatch(stripeAccountClearError()),
  onPayoutDetailsSubmit: (values, isUpdateCall) =>
    dispatch(savePayoutDetails(values, isUpdateCall)),
  onGetStripeConnectAccountLink: params => dispatch(getStripeConnectAccountLink(params)),
  onRemoveListingImage: imageId => dispatch(removeListingImage(imageId)),
  onUploadFile: (file, tempId) => dispatch(uploadFile(file, tempId)),
  onClearUploadedFiles: tempIds => dispatch(clearUploadedFiles(tempIds)),
  onDownloadFile: fileAttachmentId => dispatch(downloadFile(fileAttachmentId)),
});

// Note: withRouter needs to be **outside** connect (see EditListingPage.js).
const SectionAlertForm = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(SectionAlertFormComponent);

export default SectionAlertForm;
